import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { COPPER } from "../VestaAuth";
import { B, ago, mediaSrc, uploadMedia } from "./common";
import { AuthorLine } from "./PostCard";

export const CommentFoto = ({ src, testid }) => {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <img src={mediaSrc(src)} alt="" onClick={() => setZoom(true)} className="mt-1.5 max-h-56 max-w-[70%] cursor-zoom-in rounded-xl border border-[#2A303C] object-cover" loading="lazy" data-testid={testid} />
      {zoom && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/95" onClick={() => setZoom(false)} data-testid="bacheca-comment-foto-zoom">
          <img src={mediaSrc(src)} alt="" className="max-h-[92vh] max-w-[96vw] object-contain" />
        </div>
      )}
    </>
  );
};

export default function Comments({ p, onCount, onAuthor }) {
  const [list, setList] = useState(null);
  const [t, setT] = useState("");
  const [foto, setFoto] = useState(null);
  const [up, setUp] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { api.get(`${B}/posts/${p.id}/comments`).then((r) => setList(r.data)).catch(() => setList([])); }, [p.id]);
  const pick = async (f) => {
    if (!f.type.startsWith("image/")) { toast.error("Puoi allegare solo foto"); return; }
    setUp(0);
    try { const r = await uploadMedia(f, setUp); setFoto(r.url); } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    setUp(null);
  };
  const send = async () => {
    if (!t.trim() && !foto) return;
    setBusy(true);
    try { const r = await api.post(`${B}/posts/${p.id}/comments`, { testo: t, foto }); const n = [...list, r.data]; setList(n); onCount(n.length, n); setT(""); setFoto(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Commento non inviato"); }
    setBusy(false);
  };
  const del = async (id) => {
    try { await api.delete(`${B}/comments/${id}`); const n = list.filter((c) => c.id !== id); setList(n); onCount(n.length, n); } catch { toast.error("Operazione non riuscita"); }
  };
  if (!list) return <Loader2 className="h-4 w-4 animate-spin" style={{ color: COPPER }} />;
  return (
    <div className="space-y-3" data-testid={`bacheca-comments-${p.id}`}>
      {list.map((c) => (
        <div key={c.id} className="group" data-testid={`bacheca-comment-${c.id}`}>
          <div className="flex items-start justify-between gap-2">
            <span className="flex items-center gap-2"><AuthorLine a={c.author} logo={c.author_logo} foto={c.author_foto} onAuthor={onAuthor} small /><span className="text-[11px] text-[#6B7280]">{ago(c.created_at)}</span></span>
            {c.can_delete && <button type="button" onClick={() => del(c.id)} className="text-neutral-600 opacity-60 transition-opacity hover:text-red-400 group-hover:opacity-100" data-testid={`bacheca-comment-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
          <div className="ml-9">
            {c.testo && <p className="mt-0.5 whitespace-pre-line text-[14px] text-[#E5E7EB]">{c.testo}</p>}
            {c.foto && <CommentFoto src={c.foto} testid={`bacheca-comment-foto-${c.id}`} />}
          </div>
        </div>
      ))}
      {list.length === 0 && <p className="text-xs text-neutral-500">Nessun commento: scrivi tu il primo.</p>}
      {(foto || up !== null) && (
        <div className="relative inline-block" data-testid="bacheca-comment-foto-preview">
          {foto ? <img src={mediaSrc(foto)} alt="" className="h-20 w-20 rounded-xl object-cover" /> : <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-[#2A303C] text-xs text-neutral-400"><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />{up}%</div>}
          {foto && <button type="button" onClick={() => setFoto(null)} className="absolute -right-1.5 -top-1.5 rounded-full bg-black/80 p-1" data-testid="bacheca-comment-foto-remove"><X className="h-3 w-3" /></button>}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`bacheca-comment-foto-input-${p.id}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) pick(f); }} />
        <button type="button" onClick={() => ref.current?.click()} disabled={up !== null} className="shrink-0 rounded-full p-2 text-[#9CA3AF] transition-colors hover:bg-[#1F242D] hover:text-white disabled:opacity-40" data-testid={`bacheca-comment-foto-${p.id}`} title="Aggiungi foto"><Camera className="h-5 w-5" /></button>
        <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un commento…"
          className="h-10 min-w-0 flex-1 rounded-full border border-neutral-800 bg-neutral-950 px-4 text-sm text-white outline-none focus:border-[#C17F59]" data-testid={`bacheca-comment-input-${p.id}`} />
        <button type="button" onClick={send} disabled={busy || up !== null || (!t.trim() && !foto)} className="shrink-0 rounded-full p-2.5 text-black disabled:opacity-40" style={{ background: COPPER }} data-testid={`bacheca-comment-send-${p.id}`}><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
